import {
  getRestaurantQuery,
  getPayForCheckMutation,
  getPayForCheckDoCheckoutMutation,
  getUpdatePaymentIntentMutation,
  getFetchBearerTokenQuery,
  getCreatePaymentMethodMutation
} from './graphqlQueries'
import { apolloHeaders, authHeaders } from '../../../api/guest/constants'
import { SpiConfirmPaymentMethodLineItem, SpiSdk } from '@local/spi-js'
import { getAuthenticationHeader } from '@toasttab/do-secundo-guest-authentication'

const domain = ['tables.toasttab.com', 'booking.toasttab.com'].includes(
  window.location.hostname
)
  ? 'ws.toasttab.com'
  : 'ws-preprod.eng.toasttab.com'

async function fetchGraphql(query: string, variables: object) {
  const authHeadersSafe = authHeaders(
    await getAuthenticationHeader().catch(() => {
      return null
    })
  )

  const body = JSON.stringify({ query, variables })
  const options = {
    method: 'post',
    headers: {
      'Content-Type': 'application/json',
      ...apolloHeaders,
      ...authHeadersSafe
    },
    body,
    credentials: 'include'
  }
  return fetch(
    `https://${domain}/do-federated-gateway/v1/graphql`,
    // @ts-ignore
    options
  ).then(async (res) => {
    const ret = await res.json()
    if (res.status !== 200) {
      throw ret
    }
    return ret
  })
}

export async function fetchRestaurantV2(restaurantGuid: string) {
  const res = await fetchGraphql(getRestaurantQuery(), {
    restaurantId: restaurantGuid
  })
  return res.data.restaurantV2
}

export async function fetchBearerToken(): Promise<string> {
  const res = await fetchGraphql(getFetchBearerTokenQuery(), {})
  if (res.data.pay.bearerToken.__typename === 'PayBearerTokenError') {
    throw new Error(res.data.pay.bearerToken.code)
  }
  return res.data.pay.bearerToken.token
}

export async function fetchCreatePaymentIntent(params: {
  checkId: string
  orderId: string
  restaurantId: string
}): Promise<{
  id: string
  sessionSecret: string
  intentAmount: {
    amount: number
    currency: string
  }
}> {
  // It's a mutation, but we treat it like a resource and hold it in cache
  const res = await fetchGraphql(getCreatePaymentMethodMutation(), {
    createPaymentIntentInput: {
      checkId: params.checkId,
      orderId: params.orderId,
      restaurantId: params.restaurantId
    }
  })

  if (
    res.data.pay.createPaymentIntentByCheck.__typename ===
    'PayPaymentIntentError'
  ) {
    throw new Error(res.data.pay.createPaymentIntentByCheck.code)
  }
  return res.data.pay.createPaymentIntentByCheck
}

export async function payDeposit(
  restaurantGuid: string,
  email: string | null,
  checkGuid: string,
  orderGuid: string,
  creditCardData: any
) {
  const res = await fetchGraphql(getPayForCheckMutation(), {
    input: {
      checkGuid,
      orderGuid,
      restaurantGuid,
      newCardInput: creditCardData,
      /** This mutation REQUIRES a string */
      email: email || '',
      tipAmount: 0
    }
  })

  if (res.data.payForCheck.__typename === 'PayForCheckError') {
    throw res.data.payForCheck
  }

  return res
}

export async function payDepositSpi(
  params: {
    checkId: string
    orderId: string
    restaurantId: string
    paymentIntentId: string
    email: string
    lineItems: SpiConfirmPaymentMethodLineItem[]
  },
  spiSdk: SpiSdk,
  trackEvents: {
    onCreatePaymentMethodSuccess(): void
    onCreatePaymentMethodError(): void
    onUpdatePaymentIntentSuccess(): void
    onUpdatePaymentIntentError(): void
    onConfirmPaymentIntentSuccess(): void
    onConfirmPaymentIntentError(): void
  }
) {
  // create payment method...
  const { paymentMethodId, paymentMethodEmail: _paymentMethodEmail } =
    await new Promise<{
      paymentMethodId: string
      paymentMethodEmail: string | null
    }>(async (res, rej) => {
      try {
        await spiSdk.createPaymentMethod(
          async function onSuccess(successEvent) {
            // even though you're told to, don't put async work in here; we can't trust createPaymentMethod to handle it well -- for some reason currently consumes in a structure assuming its own errors
            if (successEvent.content.paymentMethodId) {
              res({
                paymentMethodId: successEvent.content.paymentMethodId,
                paymentMethodEmail:
                  successEvent.content.billingDetails?.email ?? null
              })
            } else {
              rej(successEvent.content.error)
            }
          },
          async function onFailure(f) {
            rej(f)
          },
          params.lineItems
        )
      } catch (e) {
        rej(e)
      }
    })
      .then((res) => {
        trackEvents.onCreatePaymentMethodSuccess()
        return res
      })
      .catch((e) => {
        trackEvents.onCreatePaymentMethodError()
        throw e
      })

  // update intent...
  await fetchGraphql(getUpdatePaymentIntentMutation(), {
    updatePaymentIntentInput: {
      checkId: params.checkId,
      orderId: params.orderId,
      paymentIntentId: params.paymentIntentId,
      paymentMethodId: paymentMethodId,
      restaurantId: params.restaurantId,
      tipAmount: {
        amount: '0.00',
        currency: 'USD'
      },
      enableConfirmRetry: true,
      fraud: {
        email: params.email
      }
    }
  })
    .then((res) => {
      if (res.errors && res.errors.length > 0) {
        // Throw first entry in array of errors
        throw res.errors[0]
      }
      trackEvents.onUpdatePaymentIntentSuccess()
    })
    .catch((e) => {
      trackEvents.onUpdatePaymentIntentError()
      throw e
    })

  // confirm payment method...
  await new Promise<void>(async (res, rej) => {
    await spiSdk.confirmIntent(
      async function onSuccess(e) {
        // even though you're told to, don't put async work in here; we can't trust confirmPayment to handle it well -- for some reason currently consumes in a structure assuming its own errors
      },
      async function onFailure(f) {
        rej(f)
      },
      { email: params.email }
    )
    res()
  })
    .catch((e) => {
      trackEvents.onConfirmPaymentIntentError()
      throw e
    })
    .then((res) => {
      trackEvents.onConfirmPaymentIntentSuccess()
      return res
    })

  // and pay!
  const res = await fetchGraphql(getPayForCheckDoCheckoutMutation(), {
    payCloseoutByCheckInput: {
      checkId: params.checkId,
      orderId: params.orderId,
      restaurantId: params.restaurantId,
      paymentIntentId: params.paymentIntentId,
      tipAmount: {
        amount: '0.00',
        currency: 'USD'
      },
      email: params.email,
      options: {
        sendServerNotification: false,
        sendEmailReceipt: true
      }
    }
  })

  if (res.data.pay.closeoutByCheck.__typename === 'PayCloseoutByCheckError') {
    throw new Error(res.data.pay.closeoutByCheck.code)
  }

  return res.data.pay.closeoutByCheck
}
